import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Divider from '@mui/material/Divider';
import Icon from '@mui/material/Icon';
import IconButton from '@mui/material/IconButton';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import Collapse from '@mui/material/Collapse';
import { alpha, useTheme as useMuiTheme } from '@mui/material/styles';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';

import { useAttendanceQueue } from './useAttendanceQueue';
import { errText } from '../../utils/attendanceUtils';
import { minToHm } from '../../utils/attendanceUtils';
import { AttendanceMarkModal } from '../../AttendanceMarkModal';
import { useMediaQuery } from '@mui/material';

export function AttendanceQueueModal({
  open,
  onClose,
  from,
  to,
  trainerId,
  onPick,
  q,
  limit = 300,
  trainersById,
  fetchParticipants,
  onWeeklyRefresh,
}) {
  const theme = useMuiTheme();
  const isDark = theme.palette.mode === 'dark';
  const handleClose = async () => {
    setSelected(null); // ✅ önce mark modal kapansın
    onClose?.(); // ✅ sonra queue kapansın
    await Promise.resolve(onWeeklyRefresh?.());
  };

  const { loading, items, count, error, refresh } = useAttendanceQueue({
    open,
    from,
    to,
    trainerId,
    q,
    limit,
  });
  const isSmDown = useMediaQuery(theme.breakpoints.down('sm'));

  // debug: raw json toggle
  const [showRaw, setShowRaw] = useState(false);
  // trainer filter (client-side)
  const [trainerFilter, setTrainerFilter] = useState('ALL'); // 'ALL' | trainerId string

  // seçilen item -> AttendanceMarkModal aç
  const [selected, setSelected] = useState(null);
  const trainerOptions = useMemo(() => {
    const list = Array.isArray(items) ? items : [];
    const map = new Map();

    list.forEach((it) => {
      const id = it?.trainerId;
      if (id == null) return;

      const key = String(id);
      if (map.has(key)) return;

      const name =
        it?.trainerNameSnapshot ||
        it?.trainerName ||
        trainersById?.get?.(key)?.name ||
        `Trainer #${key}`;

      map.set(key, { id: key, name });
    });

    // alfabetik
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name, 'tr'));
  }, [items, trainersById]);

  const filteredItems = useMemo(() => {
    const list = Array.isArray(items) ? items : [];
    if (!trainerFilter || trainerFilter === 'ALL') return list;
    return list.filter((it) => String(it?.trainerId) === trainerFilter);
  }, [items, trainerFilter]);

  const selectedSession = useMemo(() => {
    if (!selected) return null;
    const tr = trainersById?.get?.(String(selected?.trainerId));
    return {
      id: selected?.sessionId,
      dayISO: selected?.dayISO,
      startMin: selected?.startMin,
      trainerId: selected?.trainerId,
      trainerName: selected?.trainerName || tr?.name,
    };
  }, [selected, trainersById]);
  const [visibleCount, setVisibleCount] = useState(40);
  const visibleItems = useMemo(
    () => filteredItems.slice(0, visibleCount),
    [filteredItems, visibleCount]
  );

  return (
    <>
      {/* Mark modal */}
      <AttendanceMarkModal
        open={!!selected}
        onClose={() => setSelected(null)}
        session={selectedSession}
        trainersById={trainersById}
        fetchParticipants={fetchParticipants}
        onWeeklyRefresh={onWeeklyRefresh}
        onSave={async () => {
          setSelected(null);
          refresh(); // queue listesini yenile
          await Promise.resolve(onWeeklyRefresh?.());
        }}
      />

      <Dialog
        open={!!open}
        onClose={handleClose}
        fullWidth
        fullScreen={isSmDown}
        maxWidth={isSmDown ? false : 'sm'}
        PaperProps={{
          sx: {
            borderRadius: isSmDown ? 0 : '22px',
            overflow: 'hidden',
            border: `1px solid ${alpha(theme.palette.info.main, 0.14)}`,
            background: isDark ? alpha('#0b1220', 0.94) : alpha('#ffffff', 0.99),
            backgroundImage: 'none',
            boxShadow: '0 22px 60px rgba(0,0,0,.16), 0 10px 22px rgba(0,0,0,.12)',
          },
        }}
        sx={{
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0,0,0,.45)',
            backdropFilter: 'blur(6px)',
          },
        }}
      >
        <DialogTitle sx={{ pb: 1.1 }}>
          <MDBox display="flex" alignItems="center" justifyContent="space-between" gap={1}>
            <MDBox display="flex" alignItems="center" gap={1} sx={{ minWidth: 0 }}>
              <MDBox
                sx={{
                  width: 42,
                  height: 42,
                  borderRadius: '16px',
                  display: 'grid',
                  placeItems: 'center',
                  background: alpha(theme.palette.info.main, 0.14),
                  border: `1px solid ${alpha(theme.palette.info.main, 0.22)}`,
                  flex: '0 0 auto',
                }}
              >
                <Icon sx={{ fontSize: 20, color: theme.palette.info.main }}>
                  playlist_add_check
                </Icon>
              </MDBox>

              <MDBox sx={{ minWidth: 0 }}>
                <MDTypography variant="h6" fontWeight="bold" sx={{ lineHeight: 1.1 }} noWrap>
                  Yoklama Kuyruğu
                </MDTypography>
              </MDBox>
            </MDBox>

            <MDBox sx={{ display: 'flex', alignItems: 'center', gap: 0.7 }}>
              {loading ? (
                <Chip
                  size="small"
                  icon={<CircularProgress size={14} />}
                  label="Yükleniyor"
                  sx={{ height: 24, borderRadius: '999px', fontWeight: 900 }}
                />
              ) : (
                <Chip
                  size="small"
                  label={`${filteredItems?.length ?? 0} / ${count} bekleyen`}
                  sx={{
                    height: 24,
                    borderRadius: '999px',
                    bgcolor: alpha(theme.palette.info.main, 0.1),
                    border: `1px solid ${alpha(theme.palette.info.main, 0.18)}`,
                    fontWeight: 900,
                  }}
                />
              )}

              <Tooltip title="Yenile" placement="top">
                <span>
                  <IconButton
                    onClick={refresh}
                    disabled={loading}
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '14px',
                      border: `1px solid ${alpha(theme.palette.info.main, 0.22)}`,
                      bgcolor: alpha(theme.palette.info.main, 0.08),
                      '&:hover': { bgcolor: alpha(theme.palette.info.main, 0.12) },
                    }}
                  >
                    <Icon sx={{ color: theme.palette.info.main }}>refresh</Icon>
                  </IconButton>
                </span>
              </Tooltip>

              <IconButton
                onClick={handleClose}
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '14px',
                  border: `1px solid ${alpha(theme.palette.error.main, 0.22)}`,
                  bgcolor: alpha(theme.palette.error.main, 0.1),
                  '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.16) },
                }}
              >
                <Icon sx={{ color: alpha(theme.palette.error.main, 0.9) }}>close</Icon>
              </IconButton>
            </MDBox>
          </MDBox>
        </DialogTitle>

        <Divider sx={{ borderColor: alpha(theme.palette.info.main, 0.18) }} />

        <DialogContent
          sx={{
            pt: 1.1,
            pb: 1.0,
            maxHeight: isSmDown ? 'calc(100vh - 140px)' : 520, // header+actions hariç
            overflow: 'auto',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          {error ? (
            <MDBox
              sx={{
                mb: 1.1,
                p: 1.1,
                borderRadius: '16px',
                border: `1px solid ${alpha(theme.palette.error.main, 0.22)}`,
                bgcolor: alpha(theme.palette.error.main, 0.08),
              }}
            >
              <MDTypography
                variant="button"
                fontWeight="bold"
                sx={{ color: theme.palette.error.main }}
              >
                Liste alınamadı
              </MDTypography>
              <MDTypography variant="caption" sx={{ opacity: 0.9, display: 'block', mt: 0.4 }}>
                {errText(error)}
              </MDTypography>
            </MDBox>
          ) : null}
          {/* Trainer filter */}
          {trainerOptions.length ? (
            <MDBox
              sx={{
                position: 'sticky',
                top: 0,
                zIndex: 2,
                mb: 1.0,
                pt: 0.6,
                pb: 0.8,
                backdropFilter: 'blur(10px)',
                background: isDark ? alpha('#0b1220', 0.92) : alpha('#fff', 0.92),
                borderBottom: `1px solid ${alpha(theme.palette.info.main, 0.12)}`,
                display: 'flex',
                alignItems: 'center',
                gap: 0.8,
              }}
            >
              <FormControl fullWidth size="small">
                <InputLabel id="aq-trainer-filter">Eğitmen</InputLabel>
                <Select
                  labelId="aq-trainer-filter"
                  label="Eğitmen"
                  value={trainerFilter}
                  onChange={(e) => setTrainerFilter(String(e.target.value))}
                >
                  <MenuItem value="ALL">Tümü</MenuItem>
                  {trainerOptions.map((tr) => (
                    <MenuItem key={tr.id} value={tr.id}>
                      {tr.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {/* quick reset */}
              {trainerFilter !== 'ALL' ? (
                <Tooltip title="Filtreyi temizle">
                  <IconButton
                    onClick={() => setTrainerFilter('ALL')}
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '14px',
                      border: `1px solid ${alpha(theme.palette.info.main, 0.22)}`,
                      bgcolor: alpha(theme.palette.info.main, 0.08),
                      '&:hover': { bgcolor: alpha(theme.palette.info.main, 0.12) },
                    }}
                  >
                    <Icon sx={{ color: theme.palette.info.main }}>close</Icon>
                  </IconButton>
                </Tooltip>
              ) : null}
            </MDBox>
          ) : null}

          {/* Liste */}
          <MDBox sx={{ display: 'grid', gap: 1 }}>
            {visibleItems.map((it) => {
              const displayName = it?.memberName || '—';

              // ✅ trainer name: snapshot > map > fallback
              const trName =
                it?.trainerNameSnapshot || trainersById?.get?.(String(it?.trainerId))?.name || '—';

              // ✅ seans türü: NORMAL / TELAFI
              const st = String(it?.sessionType || '').toUpperCase();
              const isMakeup = st === 'TELAFI';

              // ✅ aktivite adı
              const act = it?.activityName || 'Seans';

              return (
                <MDBox
                  key={String(it?.participantId || `${it?.sessionId}-${it?.memberId}`)}
                  onClick={() => {
                    setSelected(it); // ✅ sadece mark aç, queue açık kalsın
                  }}
                  sx={{
                    cursor: 'pointer',
                    p: 0.9,
                    borderRadius: '14px',
                    border: `1px solid ${alpha(theme.palette.info.main, 0.12)}`,
                    bgcolor: alpha(theme.palette.info.main, 0.05),
                    '&:active': { transform: 'scale(0.998)' },
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                  }}
                >
                  <MDBox sx={{ minWidth: 0 }}>
                    <MDBox sx={{ display: 'flex', alignItems: 'center', gap: 0.6, minWidth: 0 }}>
                      <MDTypography variant="button" fontWeight="bold" noWrap sx={{ minWidth: 0 }}>
                        {displayName}
                      </MDTypography>

                      <Chip
                        size="small"
                        label={isMakeup ? 'Telafi' : 'Normal'}
                        sx={{
                          height: 20,
                          borderRadius: '999px',
                          fontWeight: 900,
                          fontSize: 11,
                          bgcolor: alpha(
                            isMakeup ? theme.palette.success.main : theme.palette.info.main,
                            0.12
                          ),
                          border: `1px solid ${alpha(
                            isMakeup ? theme.palette.success.main : theme.palette.info.main,
                            0.2
                          )}`,
                        }}
                      />
                    </MDBox>

                    <MDTypography variant="caption" sx={{ opacity: 0.82, display: 'block' }} noWrap>
                      {String(it?.dayISO || '—').slice(0, 10)} • {minToHm(it?.startMin || 0)} •{' '}
                      {trName}
                    </MDTypography>
                  </MDBox>
                  {/* sağ taraf: mini action */}
                  <MDBox sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: '0 0 auto' }}>
                    <Icon sx={{ fontSize: 18, color: alpha(theme.palette.warning.main, 0.95) }}>
                      fact_check
                    </Icon>
                    <Icon sx={{ fontSize: 20, opacity: 0.55 }}>chevron_right</Icon>
                  </MDBox>
                </MDBox>
              );
            })}
          </MDBox>

          {filteredItems.length > visibleCount ? (
            <MDButton
              variant="outlined"
              color="info"
              onClick={() => setVisibleCount((n) => n + 40)}
              sx={{
                borderRadius: '14px',
                textTransform: 'none',
                fontWeight: 900,
                mt: 1.2,
              }}
              fullWidth
            >
              Daha fazla yükle ({visibleCount}/{filteredItems.length})
            </MDButton>
          ) : null}

          {/* RAW JSON debug */}
          <Collapse in={showRaw}>
            <MDBox
              sx={{
                mt: 1.2,
                p: 1.2,
                borderRadius: '16px',
                border: `1px solid ${alpha(theme.palette.info.main, 0.14)}`,
                bgcolor: alpha('#000', 0.04),
              }}
            >
              <MDTypography variant="caption" fontWeight="bold" sx={{ display: 'block', mb: 0.7 }}>
                Raw JSON (debug)
              </MDTypography>
              <pre style={{ margin: 0, fontSize: 12, whiteSpace: 'pre-wrap' }}>
                {JSON.stringify(items, null, 2)}
              </pre>
            </MDBox>
          </Collapse>
        </DialogContent>

        <Divider sx={{ borderColor: alpha(theme.palette.info.main, 0.18) }} />

        <DialogActions sx={{ p: 2, gap: 1 }}>
          <MDButton
            variant="outlined"
            color="info"
            onClick={handleClose}
            sx={{
              borderRadius: '16px',
              textTransform: 'none',
              fontWeight: 900,
              height: 46,
              flex: 1,
            }}
          >
            Kapat
          </MDButton>

          <MDButton
            variant="outlined"
            color="info"
            onClick={() => setShowRaw((v) => !v)}
            sx={{
              borderRadius: '16px',
              textTransform: 'none',
              fontWeight: 900,
              height: 46,
              flex: 1,
            }}
          >
            {showRaw ? 'Raw Gizle' : 'Raw Göster'}
          </MDButton>
        </DialogActions>
      </Dialog>
    </>
  );
}

AttendanceQueueModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onPick: PropTypes.func,
  from: PropTypes.string,
  to: PropTypes.string,
  trainerId: PropTypes.number,
  q: PropTypes.string,
  limit: PropTypes.number,
  onWeeklyRefresh: PropTypes.func,
  trainersById: PropTypes.object, // Map
  fetchParticipants: PropTypes.func, // AttendanceMarkModal ile aynı
};
